import classNames from "classnames";
import { Check } from "lucide-react";
import * as React from "react";

import { type DefaultIngestionRule } from "@/shared/domain/ingestionRules";
import { useDefaultIngestionRules } from "@/shared/domain/ingestionRules/hooks";
import { Combobox, type ComboboxFilter } from "@/shared/foundation/primitives";

import styles from "./DefaultRulePicker.module.css";

interface DefaultRuleGroup {
  category: string;
  items: readonly DefaultIngestionRule[];
}

/** Groups and their rules keep the order the server lists the defaults in. */
function groupByCategory(
  defaults: readonly DefaultIngestionRule[],
): DefaultRuleGroup[] {
  const groups = new Map<string, DefaultIngestionRule[]>();
  for (const rule of defaults) {
    const group = groups.get(rule.category);
    if (group === undefined) {
      groups.set(rule.category, [rule]);
    } else {
      group.push(rule);
    }
  }
  return [...groups].map(([category, items]) => ({ category, items }));
}

/** Matches a default's name, an include pattern, or its action's name. */
const matchesDefault: ComboboxFilter<DefaultIngestionRule> = (rule, query) => {
  const needle = query.trim().toLowerCase();
  return [
    rule.name,
    ...rule.include_patterns,
    rule.invocation.action.name,
  ].some((text) => text.toLowerCase().includes(needle));
};

/**
 * Starts a new rule from one of Roboto's defaults, grouped by the kind of data
 * it ingests. A default the org already has is listed as "Added" and can't be
 * picked. Renders nothing until the defaults load.
 */
export function DefaultRulePicker({
  onPick,
  className,
}: {
  onPick: (rule: DefaultIngestionRule) => void;
  className?: string;
}) {
  const defaults = useDefaultIngestionRules();
  const [picked, setPicked] = React.useState<DefaultIngestionRule | null>(null);
  const titleId = React.useId();
  const groups = React.useMemo(
    () => groupByCategory(defaults.data ?? []),
    [defaults.data],
  );
  if (groups.length === 0) {
    return null;
  }
  return (
    <section
      className={classNames(styles.picker, className)}
      data-testid="default-rule-picker"
    >
      <h2 id={titleId} className={styles.title}>
        Start from a Roboto default{" "}
        <span className={styles.optional}>(optional)</span>
      </h2>
      <Combobox<DefaultIngestionRule>
        items={groups}
        value={picked}
        onValueChange={(rule) => {
          setPicked(rule);
          if (rule !== null) {
            onPick(rule);
          }
        }}
        filter={matchesDefault}
        itemToStringLabel={(rule) => rule.name}
        isItemEqualToValue={(item, value) => item.name === value.name}
        autoHighlight
        size="lg"
      >
        <Combobox.TriggerInput
          placeholder="Search by name, pattern or action"
          aria-labelledby={titleId}
        />
        <Combobox.Content>
          <Combobox.Empty />
          <Combobox.List>
            {(group: DefaultRuleGroup) => (
              <Combobox.Group key={group.category} items={group.items}>
                <Combobox.GroupLabel>{group.category}</Combobox.GroupLabel>
                <Combobox.Collection>
                  {(rule: DefaultIngestionRule) => (
                    <Combobox.Item
                      key={rule.name}
                      value={rule}
                      disabled={rule.already_present}
                      className={styles.row}
                    >
                      <span className={styles.name}>
                        <Combobox.MatchText text={rule.name} />
                      </span>
                      <code className={styles.code}>
                        <Combobox.MatchText
                          text={rule.include_patterns.join(", ")}
                        />
                      </code>
                      <code className={styles.code}>
                        <Combobox.MatchText
                          text={rule.invocation.action.name}
                        />
                      </code>
                      <span className={styles.added}>
                        {rule.already_present && (
                          <>
                            <Check size={14} aria-hidden="true" /> Added
                          </>
                        )}
                      </span>
                    </Combobox.Item>
                  )}
                </Combobox.Collection>
              </Combobox.Group>
            )}
          </Combobox.List>
        </Combobox.Content>
      </Combobox>
      {picked?.needs_settings && (
        <p className={styles.note} data-testid="default-rule-settings-note">
          Check its settings under &ldquo;What ingests matching files?&rdquo;,
          such as which column holds the timestamps, so it can ingest your
          files.
        </p>
      )}
    </section>
  );
}
